import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'

import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../store/user/user'

import { type User } from '../../../models/User'

import { SelectDropdown, type SelectDropdownRef, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { UserTagDropdownItemOption } from '../../../components/SelectDropdown/UserTagDropdownItemOption'
import { UserAvatarInline } from '../../../components/UserAvatar/UserAvatarInline'

import { USER_DROPDOWN_PLACEHOLDER, zocksUserToSelectOption } from '../utils'

interface AssigneeSelectProps {
  label?: string
  roomId?: string
  initialUser?: User
  unavailableUserId?: string
  disabled?: boolean
  autoOpen?: boolean
  showOpenTasksOnlyHint?: boolean
  onAssigneeSelected: (assignee: User) => void
}

export const AssigneeSelect: React.FC<AssigneeSelectProps> = ({
  label,
  roomId,
  initialUser,
  unavailableUserId,
  disabled,
  autoOpen,
  showOpenTasksOnlyHint,
  onAssigneeSelected,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dropdownRef = useRef<SelectDropdownRef>(null)

  const [searchInput, setSearchInput] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<User | undefined>(initialUser)

  const { data: users } = useGetUsersQuery({
    accountId,
    name: searchInput,
    pageRequest: { page: 0, size: 50 },
    ...(roomId ? { roomId } : {}),
  })

  useEffect(() => {
    if (autoOpen) {
      dropdownRef.current?.focus()
    }
  }, [autoOpen])

  const options = useMemo(
    () => (users?.data ?? []).filter((e) => e.name).map((user) => ({ ...zocksUserToSelectOption(user), disabled: user.id === unavailableUserId })),
    [unavailableUserId, users],
  )
  const initialSelectedOption = useMemo(() => (initialUser ? zocksUserToSelectOption(initialUser) : null), [initialUser])

  const handleSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  const handleOptionSelected = (option: SelectOption) => {
    const user = users?.data.find((user) => user.id === option.id)
    if (!user) {
      return
    }
    setSelectedUser(user)
    onAssigneeSelected(user)
  }

  return (
    <div className='d-flex flex-column'>
      {label ? <Form.Label className='paragraph-small bold'>{label}</Form.Label> : null}
      <div className='d-flex gap-3 align-items-center'>
        <UserAvatarInline user={selectedUser} className='flex-shrink-0' />
        <SelectDropdown
          ref={dropdownRef}
          className='py-2 zocks-px-0.75'
          placeholder={USER_DROPDOWN_PLACEHOLDER}
          searchable={true}
          options={options}
          initialSelectedOption={initialSelectedOption}
          onOptionSelected={handleOptionSelected}
          onSearchInputChange={handleSearchInputChange}
          DropDownItemOptionComponent={UserTagDropdownItemOption}
          disabled={disabled}
        />
      </div>
      {showOpenTasksOnlyHint ? <div className='paragraph-small zocks-text-gray-600 mt-2'>Only open tasks can be assigned.</div> : null}
    </div>
  )
}
